import React, { useState, useEffect, useCallback } from "react";
import { Icon, Spinner, SpinnerSize } from "@fluentui/react";
import { ConversationPublic, chatHistoryService } from "../../api";
import styles from "./ChatHistory.module.css";

interface ChatHistoryProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectConversation: (conversationId: string) => void;
    onNewChat: () => void;
    currentConversationId?: string;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({
    isOpen,
    onClose,
    onSelectConversation,
    onNewChat,
    currentConversationId,
}) => {
    const [conversations, setConversations] = useState<ConversationPublic[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState("");
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const loadConversations = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await chatHistoryService.listConversations();
            setConversations(data);
        } catch (err) {
            console.error("Error loading conversations:", err);
            setError("No se pudieron cargar las conversaciones");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            loadConversations();
        }
    }, [isOpen, loadConversations]);

    const handleDelete = async (conversationId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (deletingId) return;

        setDeletingId(conversationId);
        try {
            await chatHistoryService.deleteConversation(conversationId);
            setConversations(prev => prev.filter(c => c.id !== conversationId));
        } catch (err) {
            console.error("Error deleting conversation:", err);
            setError("No se pudo eliminar la conversación");
        } finally {
            setDeletingId(null);
        }
    };

    const handleStartEdit = (conversation: ConversationPublic, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingId(conversation.id);
        setEditTitle(conversation.title);
    };

    const handleSaveEdit = async (conversationId: string) => {
        if (!editTitle.trim()) {
            setEditingId(null);
            return;
        }

        try {
            const updated = await chatHistoryService.updateConversationTitle(conversationId, editTitle.trim());
            setConversations(prev =>
                prev.map(c => (c.id === conversationId ? updated : c))
            );
        } catch (err) {
            console.error("Error updating conversation title:", err);
            setError("No se pudo actualizar el título");
        } finally {
            setEditingId(null);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent, conversationId: string) => {
        if (e.key === "Enter") {
            handleSaveEdit(conversationId);
        } else if (e.key === "Escape") {
            setEditingId(null);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return "Hoy";
        } else if (diffDays === 1) {
            return "Ayer";
        } else if (diffDays < 7) {
            return `Hace ${diffDays} días`;
        } else {
            return date.toLocaleDateString("es-NI", {
                day: "numeric",
                month: "short",
            });
        }
    };

    const groupConversationsByDate = (convs: ConversationPublic[]) => {
        const groups: { [key: string]: ConversationPublic[] } = {
            today: [],
            yesterday: [],
            thisWeek: [],
            older: [],
        };

        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

        convs.forEach(conv => {
            const date = new Date(conv.updated_at);
            if (date >= today) {
                groups.today.push(conv);
            } else if (date >= yesterday) {
                groups.yesterday.push(conv);
            } else if (date >= weekAgo) {
                groups.thisWeek.push(conv);
            } else {
                groups.older.push(conv);
            }
        });

        return groups;
    };

    const groups = groupConversationsByDate(conversations);

    const renderConversationItem = (conversation: ConversationPublic) => {
        const isEditing = editingId === conversation.id;
        const isDeleting = deletingId === conversation.id;
        const isCurrent = currentConversationId === conversation.id;

        return (
            <div
                key={conversation.id}
                className={`${styles.conversationItem} ${isCurrent ? styles.active : ""}`}
                onClick={() => !isEditing && onSelectConversation(conversation.id)}
            >
                <div className={styles.conversationIcon}>
                    <Icon iconName="Message" />
                </div>
                <div className={styles.conversationContent}>
                    {isEditing ? (
                        <input
                            type="text"
                            className={styles.editInput}
                            value={editTitle}
                            onChange={e => setEditTitle(e.target.value)}
                            onBlur={() => handleSaveEdit(conversation.id)}
                            onKeyDown={e => handleKeyDown(e, conversation.id)}
                            autoFocus
                            onClick={e => e.stopPropagation()}
                        />
                    ) : (
                        <span className={styles.conversationTitle}>{conversation.title}</span>
                    )}
                </div>
                <div className={styles.conversationActions}>
                    {!isEditing && (
                        <>
                            <button
                                className={styles.actionButton}
                                onClick={e => handleStartEdit(conversation, e)}
                                title="Editar título"
                            >
                                <Icon iconName="Edit" />
                            </button>
                            <button
                                className={`${styles.actionButton} ${styles.deleteButton}`}
                                onClick={e => handleDelete(conversation.id, e)}
                                disabled={isDeleting}
                                title="Eliminar conversación"
                            >
                                {isDeleting ? (
                                    <Spinner size={SpinnerSize.xSmall} />
                                ) : (
                                    <Icon iconName="Delete" />
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderGroup = (title: string, convs: ConversationPublic[]) => {
        if (convs.length === 0) return null;
        return (
            <div className={styles.group}>
                <div className={styles.groupTitle}>{title}</div>
                {convs.map(renderConversationItem)}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.sidebar} onClick={e => e.stopPropagation()}>
                <div className={styles.header}>
                    <h2 className={styles.title}>Historial de chats</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        <Icon iconName="Cancel" />
                    </button>
                </div>

                <button className={styles.newChatButton} onClick={onNewChat}>
                    <Icon iconName="Add" />
                    <span>Nuevo chat</span>
                </button>

                <div className={styles.conversationList}>
                    {isLoading ? (
                        <div className={styles.loadingContainer}>
                            <Spinner size={SpinnerSize.medium} label="Cargando..." />
                        </div>
                    ) : error ? (
                        <div className={styles.errorContainer}>
                            <Icon iconName="ErrorBadge" className={styles.errorIcon} />
                            <span>{error}</span>
                            <button className={styles.retryButton} onClick={loadConversations}>
                                Reintentar
                            </button>
                        </div>
                    ) : conversations.length === 0 ? (
                        <div className={styles.emptyContainer}>
                            <Icon iconName="Chat" className={styles.emptyIcon} />
                            <span>No hay conversaciones guardadas</span>
                            <p className={styles.emptyHint}>
                                Tus conversaciones aparecerán aquí cuando las guardes
                            </p>
                        </div>
                    ) : (
                        <>
                            {renderGroup("Hoy", groups.today)}
                            {renderGroup("Ayer", groups.yesterday)}
                            {renderGroup("Esta semana", groups.thisWeek)}
                            {renderGroup("Más antiguas", groups.older)}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ChatHistory;
